/**
 * Menu principal — cahier des charges oral du 23/09/2026 : « un premier menu avec trois espaces :
 * espace pédagogique, espace apprenant et espace formation ».
 *
 * Module sans React : la structure du menu est une donnée, testée (`navigation.test.ts`) ; le cadre et la
 * page d'accueil l'affichent. Ajouter un écran = ajouter une ligne ici, au bon espace.
 */
export type CleIcone = "formations" | "outils" | "apprenants" | "convention" | "dossiers" | "bpf" | "courriers" | "candidatures" | "organisme" | "coffre" | "positionnement" | "profil" | "archives" | "reception" | "aide" | "pilotage" | "veille" | "cpf" | "financements" | "arborescence" | "ambassadeur" | "audience";

export interface LienNav {
  vers: string;
  libelle: string;
  icone: CleIcone;
  /** Actif seulement sur l'adresse exacte (et non sur ses sous-pages). */
  exact?: boolean;
  aide?: string;
  /** Pastille de compteur affichée à côté du lien (nombre de messages nouveaux, par exemple). */
  compteur?: "boite-reception";
}

export type CleEspace = "pedagogique" | "apprenant" | "formation" | "administration";

export interface Espace {
  cle: CleEspace;
  titre: string;
  description: string;
  liens: LienNav[];
}

/** Les trois espaces du formateur, dans l'ordre du cahier des charges. */
export const ESPACES_FORMATEUR: readonly Espace[] = [
  {
    cle: "pedagogique",
    titre: "Espace pédagogique",
    description: "Vos formations, leurs programmes et supports, vos questionnaires. L'assistant IA se documente sur chaque sujet et vous propose des brouillons : vous restez l'auteur.",
    liens: [
      { vers: "/formations", libelle: "Mes formations", icone: "formations", aide: "Enjeux, parcours, tests et supports, coffre-fort" },
      { vers: "/outils", libelle: "Outils pédagogiques", icone: "outils", aide: "Recueil des besoins, positionnement, évaluation des acquis" },
      { vers: "/coffres", libelle: "Coffre-fort pédagogique", icone: "coffre", aide: "Par parcours : supports, tests, pièces administratives" },
    ],
  },
  {
    cle: "apprenant",
    titre: "Espace apprenant",
    description: "Vos apprenants et leurs entreprises. Chaque apprenant invité suit son parcours dans son propre espace.",
    liens: [
      { vers: "/repertoire", libelle: "Apprenants et entreprises", icone: "apprenants", aide: "Fiches réutilisables d'un dossier à l'autre" },
      { vers: "/positionnements", libelle: "Positionnements", icone: "positionnement", aide: "Inviter un apprenant : recueil, test, signature" },
    ],
  },
  {
    cle: "formation",
    titre: "Espace formation",
    description: "Le générateur de conventions et le suivi de vos dossiers, de la création au paiement.",
    liens: [
      { vers: "/dossiers/nouveau", libelle: "Générateur de conventions", icone: "convention", exact: true, aide: "Apprenant, entreprise, formation, modalité, financement" },
      { vers: "/dossiers", libelle: "Mes dossiers", icone: "dossiers", exact: true, aide: "Pipeline en sept étapes" },
      { vers: "/dossiers/arborescence", libelle: "Arborescence", icone: "arborescence", aide: "Dossier › apprenant › pièces, avec ce qui reste à faire" },
      { vers: "/bpf", libelle: "BPF", icone: "bpf" },
      { vers: "/courriers", libelle: "Boîte d'envoi", icone: "courriers" },
    ],
  },
];

const ESPACES_ADMIN: readonly Espace[] = [
  {
    cle: "formation",
    titre: "Espace formation",
    description: "Tous les dossiers de l'organisme, leur validation et leur suivi.",
    liens: [
      { vers: "/", libelle: "Tous les dossiers", icone: "dossiers", exact: true },
      { vers: "/dossiers/arborescence", libelle: "Arborescence", icone: "arborescence" },
      { vers: "/financements", libelle: "Demandes de financement", icone: "financements", aide: "CPF, FAFCEA, FIFPL, OPCO : dépôts, suivi, échéances" },
      { vers: "/coffres", libelle: "Coffres-forts pédagogiques", icone: "coffre" },
      { vers: "/positionnements", libelle: "Positionnements", icone: "positionnement" },
      { vers: "/bpf", libelle: "BPF", icone: "bpf" },
      { vers: "/courriers", libelle: "Boîte d'envoi", icone: "courriers" },
    ],
  },
  {
    cle: "administration",
    titre: "Administration",
    description: "Messages du site public, candidatures des formateurs ; identité de l'organisme, assistant IA et envoi des e-mails.",
    liens: [
      { vers: "/admin/boite-de-reception", libelle: "Boîte de réception", icone: "reception", compteur: "boite-reception", aide: "Réclamations, contacts, pré-inscriptions, droits formation" },
      { vers: "/admin/pilotage", libelle: "Pilotage", icone: "pilotage", aide: "Chiffre d'affaires porté, commission, Qualiopi" },
      { vers: "/admin/audience", libelle: "Audience du site", icone: "audience", aide: "Vues des fiches de skills4mation.com" },
      { vers: "/admin/indicateurs", libelle: "Indicateurs de résultats", icone: "pilotage", aide: "Qualiopi, indicateur 2 : calcul et publication" },
      { vers: "/admin/candidatures", libelle: "Candidatures", icone: "candidatures" },
      { vers: "/admin/veille", libelle: "Veille réglementaire", icone: "veille", aide: "Qualiopi, indicateurs 23 à 25" },
      { vers: "/admin/cpf", libelle: "CPF", icone: "cpf", aide: "Barème Mon Compte Formation, certifications" },
      { vers: "/admin/financeurs", libelle: "Financeurs", icone: "financements", aide: "Activation, canaux, délais, formulaires" },
      { vers: "/admin/ambassadeurs", libelle: "Ambassadeurs", icone: "ambassadeur", aide: "Recommandations, gains à valider et à payer" },
      { vers: "/admin/organisme", libelle: "Organisme", icone: "organisme", aide: "Identité, assistant IA, e-mails" },
    ],
  },
];

/** Liens personnels du formateur validé, sous les trois espaces (« Modification 1 » : profil, archives). */
export const LIENS_PERSONNELS_FORMATEUR: readonly LienNav[] = [
  { vers: "/mode-emploi", libelle: "Mode d'emploi", icone: "aide", aide: "Le portage pas à pas, votre rémunération" },
  { vers: "/profil", libelle: "Mon profil et candidature", icone: "profil" },
  { vers: "/ma-vitrine", libelle: "Ma page sur le site", icone: "reception", aide: "Programme ambassadeur : vues de vos fiches, demandes reçues" },
  { vers: "/ambassadeur", libelle: "Programme ambassadeur", icone: "ambassadeur", aide: "Parrainage, recommandations, vos gains" },
  { vers: "/archives", libelle: "Archives et sauvegarde", icone: "archives" },
];

/** Menu affiché dans le rail latéral : une entrée « Accueil » éventuelle, les espaces, puis les liens personnels. */
export function navigationPour(role: string, formateurValide: boolean): { accueil: LienNav | null; espaces: readonly Espace[]; personnels: readonly LienNav[] } {
  if (role === "admin") return { accueil: null, espaces: ESPACES_ADMIN, personnels: [] };
  if (role === "formateur") {
    if (!formateurValide) return { accueil: null, espaces: [{ cle: "formation", titre: "Candidature", description: "", liens: [{ vers: "/candidature", libelle: "Ma candidature", icone: "candidatures" }] }], personnels: [] };
    return { accueil: { vers: "/", libelle: "Accueil", icone: "dossiers", exact: true }, espaces: ESPACES_FORMATEUR, personnels: LIENS_PERSONNELS_FORMATEUR };
  }
  return { accueil: null, espaces: [{ cle: "apprenant", titre: "Mon espace", description: "", liens: [{ vers: "/", libelle: "Mes formations", icone: "formations", exact: true }] }], personnels: [] };
}

/**
 * Item 3.4 — « Besoin d'aide ? » : prise de rendez-vous découverte (HubSpot, lien déjà utilisé par la vitrine).
 * Pour les formateurs et les candidats seulement : ni l'administrateur ni l'apprenant (qui a son formateur).
 */
export const LIEN_RENDEZ_VOUS_AIDE = "https://meetings-eu1.hubspot.com/formatrix/rendez-vous-decouverte";
export const aideRendezVousVisible = (role: string): boolean => role === "formateur";
